import {
  expandPrescription,
  resolveOrder,
  validatePlan,
  type BlockKind,
  type PlanDraft,
  type PlanIssue,
  type PlanBlockSource,
  type PlanDaySource,
  type PlanExerciseSource,
  type PrescriptionSpec,
  type ProgressionStrategy,
  type ScheduleMode,
  type SetType,
} from '@liftx/core';
import { db, type LiftXDatabase } from '../db';
import { createIntegrityRepository } from './integrityRepository';
import { createExerciseRepository } from './exerciseRepository';
import { isLive, tombstoneById, tombstoneRows } from '../tombstone';
import type {
  PlanBlockRecord,
  PlanDayRecord,
  PlanDayScheduleRecord,
  PlanExerciseRecord,
  PlanRecord,
  PlanSetRecord,
  ProgressionRuleRecord,
} from '../types';

/**
 * Creation, editing, ordering, duplication and deletion of training plans.
 * Master Phase 5.
 *
 * No schema change: all seven tables have existed, empty, since Phase 3
 * (`src/data/types.ts` §"Plans (structures only — Phase 5 builds the
 * behaviour)"). This file is that behaviour.
 *
 * Validation, prescription expansion and ordering are never reimplemented
 * here — `@liftx/core`'s `validatePlan`/`expandPrescription`/`resolveOrder`
 * are the single source of truth, exactly as Phase 5's approved plan
 * requires. This file's job is persistence and reshaping stored rows into
 * the shapes those pure functions expect.
 *
 * `integrityRepository.deletePlan()` (Phase 3) already implements the full
 * cascade delete correctly — reused here, not duplicated.
 */

export interface NewPlanInput {
  name: string;
  notes?: string | null;
  scheduleMode?: ScheduleMode;
}

export interface PlanPatch {
  name?: string;
  notes?: string | null;
  scheduleMode?: ScheduleMode;
  isActive?: boolean;
}

export interface ExercisePatch {
  exerciseId?: string | null;
  restSeconds?: number | null;
  notes?: string | null;
}

export interface ProgressionRuleInput {
  strategy: ProgressionStrategy;
  incrementG: number;
  roundingG: number;
  enabled: boolean;
}

export class PlanValidationError extends Error {
  constructor(public readonly issues: string[]) {
    super(issues.join('; '));
    this.name = 'PlanValidationError';
  }
}

export interface PlanRepository {
  // ── Plans ────────────────────────────────────────────────────────────
  listPlans(options?: { includeArchived?: boolean }): Promise<PlanRecord[]>;
  getPlan(id: string): Promise<PlanRecord | undefined>;
  createPlan(input: NewPlanInput): Promise<PlanRecord>;
  updatePlan(id: string, patch: PlanPatch): Promise<PlanRecord>;
  setArchived(id: string, isArchived: boolean): Promise<void>;
  deletePlan(id: string): Promise<void>;
  /** How many sessions were built from this plan — the Phase 4B-style archive-vs-delete gate. */
  usageCount(id: string): Promise<number>;
  /** Deep copy of an entire plan: every day/block/exercise/set/rule, fresh ids, source untouched. */
  duplicatePlan(id: string): Promise<PlanRecord>;

  // ── Simplified template flow (UI/navigation polish pass) ──────────────
  // A plan is presented to the user as a flat list of exercises — never as
  // days/blocks to configure. Under the hood every plan still has exactly
  // one `planDays` row and one `planBlocks` row (kind: 'single') per
  // exercise, since `startFromPlanDay`/`materialiseSession` are keyed on a
  // day, and a 'single' block sharing >1 exercise round-robins them like a
  // superset (`superset.ts`'s cursor doesn't branch on `kind`). These
  // methods manage that structure so no screen has to know it exists; the
  // full day/block API above remains fully intact and untouched underneath.
  /** Creates the plan and its one implicit day in a single transaction. */
  createSimplePlan(input: NewPlanInput): Promise<{ plan: PlanRecord; dayId: string }>;
  /** The plan's one implicit day (first by orderIndex), if it has one yet. */
  getPrimaryDayId(planId: string): Promise<string | undefined>;
  /** Every exercise across the plan's one day, in display order. */
  listPlanExercises(planId: string): Promise<PlanExerciseRecord[]>;
  /** Creates the exercise's own single-exercise block and a default 3×8 prescription. */
  addExerciseToPlan(planId: string, exerciseId: string): Promise<PlanExerciseRecord>;
  /** Removes the exercise and its now-empty parent block. */
  removePlanExercise(planExerciseId: string): Promise<void>;
  /** Reorders exercises by reordering their underlying (1:1) blocks. */
  reorderPlanExercises(planId: string, orderedPlanExerciseIds: string[]): Promise<void>;

  // ── Days ─────────────────────────────────────────────────────────────
  listDays(planId: string): Promise<PlanDayRecord[]>;
  addDay(planId: string, name: string): Promise<PlanDayRecord>;
  updateDay(id: string, patch: { name?: string }): Promise<PlanDayRecord>;
  removeDay(id: string): Promise<void>;
  reorderDays(planId: string, orderedDayIds: string[]): Promise<void>;
  /** Copies structure (blocks/exercises/sets/rules) but never the weekday schedule — a new day starts unscheduled. */
  duplicateDay(dayId: string, targetPlanId?: string): Promise<PlanDayRecord>;

  // ── Schedules (weekly mode only) ────────────────────────────────────
  getDaySchedule(dayId: string): Promise<number[]>;
  /** Replaces this day's whole weekday set in one write. */
  setDaySchedule(dayId: string, weekdays: number[]): Promise<void>;

  // ── Blocks ───────────────────────────────────────────────────────────
  listBlocks(planDayId: string): Promise<PlanBlockRecord[]>;
  addBlock(planDayId: string, kind: BlockKind, restSeconds?: number | null): Promise<PlanBlockRecord>;
  updateBlock(id: string, patch: { kind?: BlockKind; restSeconds?: number | null }): Promise<PlanBlockRecord>;
  removeBlock(id: string): Promise<void>;
  reorderBlocks(planDayId: string, orderedBlockIds: string[]): Promise<void>;
  duplicateBlock(blockId: string, targetDayId?: string): Promise<PlanBlockRecord>;

  // ── Exercises within a block ────────────────────────────────────────
  listExercises(planBlockId: string): Promise<PlanExerciseRecord[]>;
  addExercise(planBlockId: string, exerciseId: string): Promise<PlanExerciseRecord>;
  updateExercise(id: string, patch: ExercisePatch): Promise<PlanExerciseRecord>;
  removeExercise(id: string): Promise<void>;
  reorderExercises(planBlockId: string, orderedExerciseIds: string[]): Promise<void>;

  // ── Sets ─────────────────────────────────────────────────────────────
  listSets(planExerciseId: string): Promise<PlanSetRecord[]>;
  /**
   * Replaces every set for this exercise with `expandPrescription(spec)`'s
   * output. `targetRpeBySetIndex` covers `targetRPE`, the one `planSets`
   * field `@liftx/core`'s `PrescriptionSpec` has no vocabulary for — an
   * intentionally small, repository-only overlay rather than a change to
   * core.
   */
  saveSets(
    planExerciseId: string,
    spec: PrescriptionSpec,
    targetRpeBySetIndex?: Readonly<Record<number, number | null>>,
  ): Promise<PlanSetRecord[]>;

  // ── Progression rules ────────────────────────────────────────────────
  getProgressionRule(planExerciseId: string): Promise<ProgressionRuleRecord | undefined>;
  setProgressionRule(planExerciseId: string, rule: ProgressionRuleInput): Promise<ProgressionRuleRecord>;

  // ── Validation & assembly ────────────────────────────────────────────
  /** The full plan, reshaped for `validatePlan`. Rows with no exercise (deleted — see below) are omitted. */
  getPlanDraft(planId: string): Promise<PlanDraft>;
  validate(planId: string): Promise<PlanIssue[]>;
  /**
   * One day, reshaped for `@liftx/core`'s `materialiseSession` — built now so
   * Phase 6 has a proven-correct input, but **never called** by anything in
   * this phase (Phase 5/6 boundary). Exercise slots whose `exerciseId` was
   * nulled by `integrityRepository.deleteExercise` are omitted: there is
   * nothing to materialise for a slot with no exercise.
   */
  getPlanDaySource(planDayId: string): Promise<PlanDaySource>;

  /**
   * The next day due on the single active weekly-scheduled plan, at or after
   * `fromWeekday` (1 = Monday … 7 = Sunday), wrapping to next week if none
   * remain. Master Phase 8's Dashboard "next scheduled day" card. Returns
   * `null` when there is no active plan, the active plan isn't
   * `scheduleMode: 'weekly'`, or that plan has no scheduled days at all —
   * `'free'`/`'rotation'` modes have no fixed weekday to suggest.
   */
  getNextScheduledDay(
    fromWeekday: number,
  ): Promise<{ planId: string; dayId: string; dayName: string; weekday: number } | null>;
}

/** setType/rep/duration bounds live in @liftx/core's plan.ts — no need to duplicate them here. */
function toPrescribedSets(spec: PrescriptionSpec): ReturnType<typeof expandPrescription> {
  return expandPrescription(spec);
}

export function createPlanRepository(database: LiftXDatabase = db): PlanRepository {
  const integrityRepository = createIntegrityRepository(database);
  const exerciseRepository = createExerciseRepository(database);

  /** Every plan-scoped table, for transactions that touch "the whole plan". */
  const allPlanTables = [
    database.plans,
    database.planDays,
    database.planDaySchedules,
    database.planBlocks,
    database.planExercises,
    database.planSets,
    database.progressionRules,
  ] as const;

  async function nextOrderIndex<T extends { orderIndex: number }>(rows: T[]): Promise<number> {
    return rows.reduce((max, row) => Math.max(max, row.orderIndex), -1) + 1;
  }

  /** Rewrites `orderIndex` for `table` to match `resolveOrder(currentIds, requestedIds)`. */
  async function applyOrder(
    table: LiftXDatabase['planDays'] | LiftXDatabase['planBlocks'] | LiftXDatabase['planExercises'],
    currentIds: readonly string[],
    requestedIds: readonly string[],
  ): Promise<void> {
    const finalOrder = resolveOrder(currentIds, requestedIds);
    await Promise.all(
      finalOrder.map((id, index) => (table as { update: (id: string, patch: object) => Promise<number> }).update(id, { orderIndex: index })),
    );
  }

  async function cascadeDeleteExercise(planExerciseId: string): Promise<void> {
    const now = Date.now();
    const sets = await database.planSets.where('planExerciseId').equals(planExerciseId).toArray();
    await tombstoneRows(database.planSets, sets, now);
    const rules = await database.progressionRules.where('planExerciseId').equals(planExerciseId).toArray();
    await tombstoneRows(database.progressionRules, rules, now);
    await tombstoneById(database.planExercises, planExerciseId, now);
  }

  async function cascadeDeleteBlock(planBlockId: string): Promise<void> {
    const exercises = await database.planExercises.where('planBlockId').equals(planBlockId).filter(isLive).toArray();
    for (const exercise of exercises) await cascadeDeleteExercise(exercise.id);
    await tombstoneById(database.planBlocks, planBlockId, Date.now());
  }

  async function cascadeDeleteDay(planDayId: string): Promise<void> {
    const blocks = await database.planBlocks.where('planDayId').equals(planDayId).filter(isLive).toArray();
    for (const block of blocks) await cascadeDeleteBlock(block.id);
    const now = Date.now();
    const schedules = await database.planDaySchedules.where('planDayId').equals(planDayId).toArray();
    await tombstoneRows(database.planDaySchedules, schedules, now);
    await tombstoneById(database.planDays, planDayId, now);
  }

  /** Deep-copies one exercise slot (and its sets/progression rule) onto `targetPlanBlockId`, fresh ids throughout. */
  async function copyExercise(
    source: PlanExerciseRecord,
    targetPlanBlockId: string,
    orderInBlock: number,
  ): Promise<void> {
    const newExerciseId = crypto.randomUUID();
    const copy: PlanExerciseRecord = { ...source, id: newExerciseId, planBlockId: targetPlanBlockId, orderInBlock };
    await database.planExercises.add(copy);

    const sets = await database.planSets.where('planExerciseId').equals(source.id).filter(isLive).toArray();
    await database.planSets.bulkAdd(
      sets.map((set) => ({ ...set, id: crypto.randomUUID(), planExerciseId: newExerciseId })),
    );

    const rule = await database.progressionRules.where('planExerciseId').equals(source.id).filter(isLive).first();
    if (rule !== undefined) {
      await database.progressionRules.add({ ...rule, id: crypto.randomUUID(), planExerciseId: newExerciseId });
    }
  }

  /** Deep-copies one block onto `targetPlanDayId`, appended at the end. */
  async function copyBlock(source: PlanBlockRecord, targetPlanDayId: string): Promise<PlanBlockRecord> {
    const existing = await database.planBlocks.where('planDayId').equals(targetPlanDayId).filter(isLive).toArray();
    const orderIndex = await nextOrderIndex(existing);

    const newBlockId = crypto.randomUUID();
    const copy: PlanBlockRecord = { ...source, id: newBlockId, planDayId: targetPlanDayId, orderIndex };
    await database.planBlocks.add(copy);

    const exercises = await database.planExercises.where('planBlockId').equals(source.id).filter(isLive).toArray();
    exercises.sort((a, b) => a.orderInBlock - b.orderInBlock);
    for (const [index, exercise] of exercises.entries()) await copyExercise(exercise, newBlockId, index);

    return copy;
  }

  /** Deep-copies one day (structure only — never the weekday schedule) onto `targetPlanId`, appended at the end. */
  async function copyDay(source: PlanDayRecord, targetPlanId: string): Promise<PlanDayRecord> {
    const existing = await database.planDays.where('planId').equals(targetPlanId).filter(isLive).toArray();
    const orderIndex = await nextOrderIndex(existing);

    const newDayId = crypto.randomUUID();
    const copy: PlanDayRecord = { ...source, id: newDayId, planId: targetPlanId, orderIndex };
    await database.planDays.add(copy);

    const blocks = await database.planBlocks.where('planDayId').equals(source.id).filter(isLive).toArray();
    blocks.sort((a, b) => a.orderIndex - b.orderIndex);
    for (const block of blocks) await copyBlock(block, newDayId);

    return copy;
  }

  const repository: PlanRepository = {
    async listPlans(options): Promise<PlanRecord[]> {
      const rows = await database.plans.filter(isLive).toArray();
      const filtered = options?.includeArchived === true ? rows : rows.filter((row) => !row.isArchived);
      return filtered.sort((a, b) => b.updatedAt - a.updatedAt || a.name.localeCompare(b.name));
    },

    async getPlan(id): Promise<PlanRecord | undefined> {
      const row = await database.plans.get(id);
      return row === undefined || !isLive(row) ? undefined : row;
    },

    async createPlan(input): Promise<PlanRecord> {
      if (input.name.trim().length === 0) throw new PlanValidationError(['The plan needs a name.']);

      const now = Date.now();
      const record: PlanRecord = {
        id: crypto.randomUUID(),
        name: input.name,
        notes: input.notes ?? null,
        scheduleMode: input.scheduleMode ?? 'free',
        isActive: false,
        isArchived: false,
        createdAt: now,
        updatedAt: now,
      };
      await database.plans.add(record);
      return record;
    },

    async updatePlan(id, patch): Promise<PlanRecord> {
      if (patch.name !== undefined && patch.name.trim().length === 0) {
        throw new PlanValidationError(['The plan needs a name.']);
      }
      const existing = await repository.getPlan(id);
      if (existing === undefined) throw new Error(`Plan ${id} does not exist`);

      const updated: PlanRecord = { ...existing, ...patch, updatedAt: Date.now() };
      await database.plans.put(updated);
      return updated;
    },

    async setArchived(id, isArchived): Promise<void> {
      await database.plans.update(id, { isArchived, updatedAt: Date.now() });
    },

    async deletePlan(id): Promise<void> {
      // Reused verbatim — Phase 3's cascade is already correct and already
      // tested; a second implementation here would be exactly the risk
      // Phase 5's approved plan rules out.
      await integrityRepository.deletePlan(id);
    },

    async usageCount(id): Promise<number> {
      return await database.sessions.where('planId').equals(id).filter(isLive).count();
    },

    async duplicatePlan(id): Promise<PlanRecord> {
      const source = await repository.getPlan(id);
      if (source === undefined) throw new Error(`Plan ${id} does not exist`);

      return await database.transaction('rw', allPlanTables, async () => {
        const now = Date.now();
        const copy: PlanRecord = {
          ...source,
          id: crypto.randomUUID(),
          name: `${source.name} (copy)`,
          isActive: false,
          createdAt: now,
          updatedAt: now,
        };
        await database.plans.add(copy);

        const days = await database.planDays.where('planId').equals(id).filter(isLive).toArray();
        days.sort((a, b) => a.orderIndex - b.orderIndex);
        for (const day of days) {
          const newDay = await copyDay(day, copy.id);
          // Schedule IS carried over for a whole-plan duplicate (unlike a
          // single day copy — see `copyDay`'s comment): duplicating an
          // entire plan is meant to reproduce it, schedule included.
          const schedules = await database.planDaySchedules.where('planDayId').equals(day.id).filter(isLive).toArray();
          await database.planDaySchedules.bulkAdd(
            schedules.map((row) => ({ ...row, id: crypto.randomUUID(), planDayId: newDay.id })),
          );
        }

        return copy;
      });
    },

    async createSimplePlan(input): Promise<{ plan: PlanRecord; dayId: string }> {
      if (input.name.trim().length === 0) throw new PlanValidationError(['The plan needs a name.']);

      return await database.transaction('rw', [database.plans, database.planDays], async () => {
        const plan = await repository.createPlan(input);
        const day = await repository.addDay(plan.id, plan.name);
        return { plan, dayId: day.id };
      });
    },

    async getPrimaryDayId(planId): Promise<string | undefined> {
      const days = await database.planDays.where('planId').equals(planId).filter(isLive).toArray();
      days.sort((a, b) => a.orderIndex - b.orderIndex);
      return days[0]?.id;
    },

    async listPlanExercises(planId): Promise<PlanExerciseRecord[]> {
      const dayId = await repository.getPrimaryDayId(planId);
      if (dayId === undefined) return [];

      const blocks = await database.planBlocks.where('planDayId').equals(dayId).filter(isLive).toArray();
      blocks.sort((a, b) => a.orderIndex - b.orderIndex);

      const rows: PlanExerciseRecord[] = [];
      for (const block of blocks) {
        const exercises = await database.planExercises.where('planBlockId').equals(block.id).filter(isLive).toArray();
        exercises.sort((a, b) => a.orderInBlock - b.orderInBlock);
        rows.push(...exercises);
      }
      return rows;
    },

    async addExerciseToPlan(planId, exerciseId): Promise<PlanExerciseRecord> {
      const exercise = await exerciseRepository.get(exerciseId);
      if (exercise === undefined) throw new Error(`Exercise ${exerciseId} does not exist`);

      // `database.exercises` isn't one of `allPlanTables` — every other use
      // of that constant only ever copies plan-owned rows, never looks an
      // exercise up mid-transaction. This is the first that does (via the
      // nested `addExercise()` call below), so it needs the table declared
      // explicitly: real IndexedDB throws if a transaction touches a store
      // it didn't declare up front, even to read.
      return await database.transaction('rw', [...allPlanTables, database.exercises], async () => {
        let dayId = await repository.getPrimaryDayId(planId);
        if (dayId === undefined) {
          const plan = await repository.getPlan(planId);
          if (plan === undefined) throw new Error(`Plan ${planId} does not exist`);
          const day = await repository.addDay(planId, plan.name);
          dayId = day.id;
        }

        const block = await repository.addBlock(dayId, 'single');
        const planExercise = await repository.addExercise(block.id, exerciseId);

        // The default prescription is modality-aware (Cardio Phase C5).
        //
        // Strength and hold keep the original 3x8: a sane, immediately-visible
        // default matching the mockup that showed 3x8 the instant an exercise
        // was added, not a blank prescription.
        //
        // Cardio must NOT inherit that. Reps are meaningless for a run, and
        // three of them is a prescription nobody asked for — a user adding
        // "Treadmill Run" would silently get "3 sets of 8 reps". One segment
        // with BOTH targets left null is the honest default: an empty target
        // is a prescription the user has not written yet, whereas inventing
        // "30 minutes" or "5 km" would put words in their mouth. C1's
        // `cardio_without_target` warning is what surfaces the gap, at the
        // existing validation boundary rather than by guessing here.
        await repository.saveSets(
          planExercise.id,
          exercise.modality === 'cardio'
            ? {
                mode: 'uniform',
                sets: 1,
                repMin: null,
                repMax: null,
                targetWeightG: null,
                targetDurationS: null,
                targetDistanceM: null,
              }
            : {
                mode: 'uniform',
                sets: 3,
                repMin: 8,
                repMax: 8,
                targetWeightG: null,
                targetDurationS: null,
                targetDistanceM: null,
              },
        );
        return planExercise;
      });
    },

    async removePlanExercise(planExerciseId): Promise<void> {
      await database.transaction('rw', allPlanTables, async () => {
        const planExercise = await database.planExercises.get(planExerciseId);
        if (planExercise === undefined || !isLive(planExercise)) return;
        const blockId = planExercise.planBlockId;

        await cascadeDeleteExercise(planExerciseId);

        // 1:1 in this flow — remove the now-empty block so nothing orphaned
        // is left behind. Guarded by an emptiness check, not assumed, since
        // a block from the full day/block API could still hold others.
        const remaining = await database.planExercises.where('planBlockId').equals(blockId).filter(isLive).count();
        if (remaining === 0) await tombstoneById(database.planBlocks, blockId, Date.now());
      });
    },

    async reorderPlanExercises(planId, orderedPlanExerciseIds): Promise<void> {
      const dayId = await repository.getPrimaryDayId(planId);
      if (dayId === undefined) return;

      const blocks = await database.planBlocks.where('planDayId').equals(dayId).filter(isLive).toArray();
      const blockIdByExerciseId = new Map<string, string>();
      for (const block of blocks) {
        const first = await database.planExercises.where('planBlockId').equals(block.id).filter(isLive).first();
        if (first !== undefined) blockIdByExerciseId.set(first.id, block.id);
      }

      const orderedBlockIds = orderedPlanExerciseIds
        .map((exerciseId) => blockIdByExerciseId.get(exerciseId))
        .filter((value): value is string => value !== undefined);
      await repository.reorderBlocks(dayId, orderedBlockIds);
    },

    async listDays(planId): Promise<PlanDayRecord[]> {
      const rows = await database.planDays.where('planId').equals(planId).filter(isLive).toArray();
      return rows.sort((a, b) => a.orderIndex - b.orderIndex);
    },

    async addDay(planId, name): Promise<PlanDayRecord> {
      if (name.trim().length === 0) throw new PlanValidationError(['Every day needs a name.']);

      const existing = await database.planDays.where('planId').equals(planId).filter(isLive).toArray();
      const record: PlanDayRecord = {
        id: crypto.randomUUID(),
        planId,
        name,
        orderIndex: await nextOrderIndex(existing),
      };
      await database.planDays.add(record);
      return record;
    },

    async updateDay(id, patch): Promise<PlanDayRecord> {
      if (patch.name !== undefined && patch.name.trim().length === 0) {
        throw new PlanValidationError(['Every day needs a name.']);
      }
      const existing = await database.planDays.get(id);
      if (existing === undefined || !isLive(existing)) throw new Error(`Plan day ${id} does not exist`);

      const updated: PlanDayRecord = { ...existing, ...patch };
      await database.planDays.put(updated);
      return updated;
    },

    async removeDay(id): Promise<void> {
      await database.transaction('rw', allPlanTables, async () => {
        await cascadeDeleteDay(id);
      });
    },

    async reorderDays(planId, orderedDayIds): Promise<void> {
      const existing = await database.planDays.where('planId').equals(planId).filter(isLive).toArray();
      await applyOrder(
        database.planDays,
        existing.map((row) => row.id),
        orderedDayIds,
      );
    },

    async duplicateDay(dayId, targetPlanId): Promise<PlanDayRecord> {
      const source = await database.planDays.get(dayId);
      if (source === undefined || !isLive(source)) throw new Error(`Plan day ${dayId} does not exist`);

      return await database.transaction('rw', allPlanTables, async () => {
        return await copyDay(source, targetPlanId ?? source.planId);
      });
    },

    async getDaySchedule(dayId): Promise<number[]> {
      const rows = await database.planDaySchedules.where('planDayId').equals(dayId).filter(isLive).toArray();
      return rows.map((row) => row.weekday).sort((a, b) => a - b);
    },

    async setDaySchedule(dayId, weekdays): Promise<void> {
      const unique = [...new Set(weekdays)];
      await database.transaction('rw', database.planDaySchedules, async () => {
        const now = Date.now();
        const existing = await database.planDaySchedules.where('planDayId').equals(dayId).filter(isLive).toArray();
        await tombstoneRows(database.planDaySchedules, existing, now);
        const rows: PlanDayScheduleRecord[] = unique.map((weekday) => ({
          id: crypto.randomUUID(),
          planDayId: dayId,
          weekday,
        }));
        if (rows.length > 0) await database.planDaySchedules.bulkAdd(rows);
      });
    },

    async listBlocks(planDayId): Promise<PlanBlockRecord[]> {
      const rows = await database.planBlocks.where('planDayId').equals(planDayId).filter(isLive).toArray();
      return rows.sort((a, b) => a.orderIndex - b.orderIndex);
    },

    async addBlock(planDayId, kind, restSeconds = null): Promise<PlanBlockRecord> {
      const existing = await database.planBlocks.where('planDayId').equals(planDayId).filter(isLive).toArray();
      const record: PlanBlockRecord = {
        id: crypto.randomUUID(),
        planDayId,
        orderIndex: await nextOrderIndex(existing),
        kind,
        restSeconds,
      };
      await database.planBlocks.add(record);
      return record;
    },

    async updateBlock(id, patch): Promise<PlanBlockRecord> {
      const existing = await database.planBlocks.get(id);
      if (existing === undefined || !isLive(existing)) throw new Error(`Plan block ${id} does not exist`);

      const updated: PlanBlockRecord = { ...existing, ...patch };
      await database.planBlocks.put(updated);
      return updated;
    },

    async removeBlock(id): Promise<void> {
      await database.transaction('rw', allPlanTables, async () => {
        await cascadeDeleteBlock(id);
      });
    },

    async reorderBlocks(planDayId, orderedBlockIds): Promise<void> {
      const existing = await database.planBlocks.where('planDayId').equals(planDayId).filter(isLive).toArray();
      await applyOrder(
        database.planBlocks,
        existing.map((row) => row.id),
        orderedBlockIds,
      );
    },

    async duplicateBlock(blockId, targetDayId): Promise<PlanBlockRecord> {
      const source = await database.planBlocks.get(blockId);
      if (source === undefined || !isLive(source)) throw new Error(`Plan block ${blockId} does not exist`);

      return await database.transaction('rw', allPlanTables, async () => {
        return await copyBlock(source, targetDayId ?? source.planDayId);
      });
    },

    async listExercises(planBlockId): Promise<PlanExerciseRecord[]> {
      const rows = await database.planExercises.where('planBlockId').equals(planBlockId).filter(isLive).toArray();
      return rows.sort((a, b) => a.orderInBlock - b.orderInBlock);
    },

    async addExercise(planBlockId, exerciseId): Promise<PlanExerciseRecord> {
      const exercise = await exerciseRepository.get(exerciseId);
      if (exercise === undefined) throw new Error(`Exercise ${exerciseId} does not exist`);

      const existing = await database.planExercises.where('planBlockId').equals(planBlockId).filter(isLive).toArray();
      const record: PlanExerciseRecord = {
        id: crypto.randomUUID(),
        planBlockId,
        exerciseId,
        orderInBlock: await nextOrderIndex(existing.map((row) => ({ orderIndex: row.orderInBlock }))),
        restSeconds: null,
        notes: null,
        prescriptionMode: 'uniform',
      };
      await database.planExercises.add(record);
      return record;
    },

    async updateExercise(id, patch): Promise<PlanExerciseRecord> {
      const existing = await database.planExercises.get(id);
      if (existing === undefined || !isLive(existing)) throw new Error(`Plan exercise ${id} does not exist`);

      if (patch.exerciseId !== undefined && patch.exerciseId !== null) {
        const replacement = await exerciseRepository.get(patch.exerciseId);
        if (replacement === undefined) throw new Error(`Exercise ${patch.exerciseId} does not exist`);
      }

      const updated: PlanExerciseRecord = { ...existing, ...patch };
      await database.planExercises.put(updated);
      return updated;
    },

    async removeExercise(id): Promise<void> {
      await database.transaction(
        'rw',
        [database.planExercises, database.planSets, database.progressionRules],
        async () => {
          await cascadeDeleteExercise(id);
        },
      );
    },

    async reorderExercises(planBlockId, orderedExerciseIds): Promise<void> {
      const existing = await database.planExercises.where('planBlockId').equals(planBlockId).filter(isLive).toArray();
      const finalOrder = resolveOrder(
        existing.map((row) => row.id),
        orderedExerciseIds,
      );
      await Promise.all(
        finalOrder.map((exerciseId, index) => database.planExercises.update(exerciseId, { orderInBlock: index })),
      );
    },

    async listSets(planExerciseId): Promise<PlanSetRecord[]> {
      const rows = await database.planSets.where('planExerciseId').equals(planExerciseId).filter(isLive).toArray();
      return rows.sort((a, b) => a.setIndex - b.setIndex || a.dropIndex - b.dropIndex);
    },

    async saveSets(planExerciseId, spec, targetRpeBySetIndex = {}): Promise<PlanSetRecord[]> {
      const prescribed = toPrescribedSets(spec);

      return await database.transaction('rw', database.planSets, async () => {
        const existing = await database.planSets.where('planExerciseId').equals(planExerciseId).filter(isLive).toArray();
        await tombstoneRows(database.planSets, existing, Date.now());

        const rows: PlanSetRecord[] = prescribed.map((set) => ({
          id: crypto.randomUUID(),
          planExerciseId,
          setIndex: set.setIndex,
          dropIndex: set.dropIndex,
          setType: set.setType as SetType,
          repMin: set.repMin,
          repMax: set.repMax,
          targetWeightG: set.targetWeightG,
          targetDurationS: set.targetDurationS,
          targetDistanceM: set.targetDistanceM,
          targetRPE: targetRpeBySetIndex[set.setIndex] ?? null,
        }));

        if (rows.length > 0) await database.planSets.bulkAdd(rows);
        return rows;
      });
    },

    async getProgressionRule(planExerciseId): Promise<ProgressionRuleRecord | undefined> {
      return await database.progressionRules.where('planExerciseId').equals(planExerciseId).filter(isLive).first();
    },

    async setProgressionRule(planExerciseId, rule): Promise<ProgressionRuleRecord> {
      return await database.transaction('rw', database.progressionRules, async () => {
        const existing = await database.progressionRules.where('planExerciseId').equals(planExerciseId).filter(isLive).first();
        const record: ProgressionRuleRecord = {
          id: existing?.id ?? crypto.randomUUID(),
          planExerciseId,
          ...rule,
        };
        await database.progressionRules.put(record);
        return record;
      });
    },

    async getPlanDraft(planId): Promise<PlanDraft> {
      const plan = await repository.getPlan(planId);
      if (plan === undefined) throw new Error(`Plan ${planId} does not exist`);

      const days = await database.planDays.where('planId').equals(planId).filter(isLive).toArray();
      days.sort((a, b) => a.orderIndex - b.orderIndex);

      const draftDays: PlanDraft['days'] = [];
      for (const day of days) {
        const schedules = await database.planDaySchedules.where('planDayId').equals(day.id).filter(isLive).toArray();
        const blocks = await database.planBlocks.where('planDayId').equals(day.id).filter(isLive).toArray();
        blocks.sort((a, b) => a.orderIndex - b.orderIndex);

        const draftBlocks: PlanDraft['days'][number]['blocks'] = [];
        for (const block of blocks) {
          const exercises = await database.planExercises.where('planBlockId').equals(block.id).filter(isLive).toArray();
          exercises.sort((a, b) => a.orderInBlock - b.orderInBlock);

          const draftExercises: PlanDraft['days'][number]['blocks'][number]['exercises'] = [];
          for (const planExercise of exercises) {
            // A slot whose exercise was deleted (`exerciseId === null`, set
            // by `integrityRepository.deleteExercise`) has nothing for
            // `validatePlan` to check — it is a referential-health concern
            // for the UI, not a plan-structure concern for the validator.
            if (planExercise.exerciseId === null) continue;

            const exercise = await exerciseRepository.get(planExercise.exerciseId);
            const sets = await database.planSets.where('planExerciseId').equals(planExercise.id).filter(isLive).toArray();
            sets.sort((a, b) => a.setIndex - b.setIndex || a.dropIndex - b.dropIndex);

            draftExercises.push({
              id: planExercise.id,
              exerciseId: planExercise.exerciseId,
              ...(exercise !== undefined ? { modality: exercise.modality } : {}),
              sets: sets.map((set) => ({
                setIndex: set.setIndex,
                dropIndex: set.dropIndex,
                setType: set.setType,
                repMin: set.repMin,
                repMax: set.repMax,
                targetWeightG: set.targetWeightG,
                targetDurationS: set.targetDurationS,
                // `?? null` because rows written before Cardio C1 have no
                // such property at all — Dexie returns them unchanged.
                targetDistanceM: set.targetDistanceM ?? null,
              })),
            });
          }

          draftBlocks.push({ id: block.id, kind: block.kind, exercises: draftExercises });
        }

        draftDays.push({
          id: day.id,
          name: day.name,
          weekdays: schedules.map((row) => row.weekday),
          blocks: draftBlocks,
        });
      }

      return { name: plan.name, days: draftDays };
    },

    async validate(planId): Promise<PlanIssue[]> {
      const draft = await repository.getPlanDraft(planId);
      return validatePlan(draft);
    },

    async getPlanDaySource(planDayId): Promise<PlanDaySource> {
      const day = await database.planDays.get(planDayId);
      if (day === undefined || !isLive(day)) throw new Error(`Plan day ${planDayId} does not exist`);

      const blocks = await database.planBlocks.where('planDayId').equals(planDayId).filter(isLive).toArray();
      blocks.sort((a, b) => a.orderIndex - b.orderIndex);

      const blockSources: PlanBlockSource[] = [];
      for (const block of blocks) {
        const exercises = await database.planExercises.where('planBlockId').equals(block.id).filter(isLive).toArray();
        exercises.sort((a, b) => a.orderInBlock - b.orderInBlock);

        const exerciseSources: PlanExerciseSource[] = [];
        for (const planExercise of exercises) {
          // Same rule as `getPlanDraft`: a slot with no exercise cannot be
          // materialised into a session, so it is omitted here too.
          if (planExercise.exerciseId === null) continue;

          const exercise = await exerciseRepository.get(planExercise.exerciseId);
          if (exercise === undefined) continue;

          const sets = await database.planSets.where('planExerciseId').equals(planExercise.id).filter(isLive).toArray();
          sets.sort((a, b) => a.setIndex - b.setIndex || a.dropIndex - b.dropIndex);

          exerciseSources.push({
            planExerciseId: planExercise.id,
            exerciseId: planExercise.exerciseId,
            orderInBlock: planExercise.orderInBlock,
            modality: exercise.modality,
            loadType: exercise.loadType,
            restSeconds: planExercise.restSeconds,
            sets: sets.map((set) => ({
              setIndex: set.setIndex,
              dropIndex: set.dropIndex,
              setType: set.setType,
              repMin: set.repMin,
              repMax: set.repMax,
              targetWeightG: set.targetWeightG,
              targetDurationS: set.targetDurationS,
              // `?? null` because rows written before Cardio C1 have no such
              // property at all — Dexie returns them unchanged.
              targetDistanceM: set.targetDistanceM ?? null,
            })),
          });
        }

        blockSources.push({
          planBlockId: block.id,
          orderIndex: block.orderIndex,
          kind: block.kind,
          restSeconds: block.restSeconds,
          exercises: exerciseSources,
        });
      }

      return {
        planId: day.planId,
        planDayId: day.id,
        name: day.name,
        blocks: blockSources,
      };
    },

    async getNextScheduledDay(fromWeekday) {
      const activePlans = (await database.plans.filter(isLive).toArray()).filter(
        (plan) => plan.isActive && !plan.isArchived && plan.scheduleMode === 'weekly',
      );
      const plan = activePlans[0];
      if (plan === undefined) return null;

      const days = await database.planDays.where('planId').equals(plan.id).filter(isLive).toArray();
      const candidates: { dayId: string; dayName: string; weekday: number }[] = [];
      for (const day of days) {
        const schedules = await database.planDaySchedules.where('planDayId').equals(day.id).filter(isLive).toArray();
        for (const schedule of schedules) {
          candidates.push({ dayId: day.id, dayName: day.name, weekday: schedule.weekday });
        }
      }
      if (candidates.length === 0) return null;

      // Nearest at-or-after `fromWeekday`, else the earliest weekday next
      // week — computed as a distance so both cases share one comparison.
      const distance = (weekday: number): number => (weekday >= fromWeekday ? weekday - fromWeekday : weekday - fromWeekday + 7);
      candidates.sort((a, b) => distance(a.weekday) - distance(b.weekday) || a.weekday - b.weekday);
      const next = candidates[0]!;

      return { planId: plan.id, dayId: next.dayId, dayName: next.dayName, weekday: next.weekday };
    },
  };

  return repository;
}

export const planRepository = createPlanRepository();
